import React, { useState, useEffect } from "react";
import { useRef } from "react";
import SideBar from "../components/sidebar";
import Header from "../components/header";
import "../assets/CSS/dashboard.css";
import "../assets/CSS/forms.css";
import API_BASE_URL from "../config";
import "../assets/CSS/loader.css";
import { authFetch } from "../utility/AuthFetch";
import FileUploader from "../components/fileUpload";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "../utility/CustomImageBlot";

function Lead_Excel() {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loader, setLoader] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [subject, setSubject] = useState("");
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [editorImages, setEditorImages] = useState({});

  const { quill, quillRef } = useQuill({
    theme: "snow",
    placeholder: "Enter Mail Body...",
    modules: {
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        [{ font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        ["link", "image"],
        ["clean"],
      ],
    },
  });

  useEffect(() => {
    if (!quill) return;

    quill.getModule("toolbar").addHandler("image", imageHandler);
  }, [quill]);

  const imageHandler = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      const imageId = crypto.randomUUID();
      if (!file || !quill) return;

      try {
        const preview = URL.createObjectURL(file);

        setEditorImages((prev) => ({
          ...prev,
          [imageId]: {
            file,
            preview,
          },
        }));

        const range = quill.getSelection(true) || {
          index: quill.getLength(),
        };

        quill.insertEmbed(range.index, "image", {
          src: preview,
          imageId,
        });
        quill.setSelection(range.index + 1);
      } catch (err) {
        console.error(err);
        alert("Image upload failed");
      }
    };
  };

  useEffect(() => {
    if (!subject) {
      setSubjectOptions([]);
      return;
    }

    const delayDebounce = setTimeout(() => {
      authFetch(`${API_BASE_URL}/api/subject-options?query=${subject}`)
        .then((res) => res.json())
        .then((data) => {
          setSubjectOptions(data.subjects.map((d) => d.subject));
        })
        .catch((err) => console.error("Error fetching:", err));
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [subject]);

  const handleSubmit = async (e) => {
    setLoader(true);
    e.preventDefault();
    setIsSubmitting(true);

    if (!formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      setIsSubmitting(false);
      setLoader(false);
      return;
    }

    if (!quill) {
      alert("Editor is still loading. Please try again.");
      setIsSubmitting(false);
      setLoader(false);
      return;
    }

    const delta = quill.getContents();

    const hasContent = delta.ops.some((op) => {
      // Text
      if (typeof op.insert === "string") {
        return op.insert.trim().length > 0;
      }

      // Images, videos, formulas, etc.
      return op.insert && typeof op.insert === "object";
    });

    if (!hasContent) {
      alert("Mail Body is required");
      setIsSubmitting(false);
      setLoader(false);
      return;
    }

    const formData = new FormData(formRef.current);
    const div = document.createElement("div");
    div.innerHTML = quill.root.innerHTML;

    div.querySelectorAll("img").forEach((img) => {
      const imageId = img.dataset.imageId;

      if (imageId) {
        img.src = `cid:${imageId}`;
      }
    });

    formData.append("content", div.innerHTML);

    attachments.forEach((file) => formData.append("attachments", file));
    Object.entries(editorImages).forEach(([imageId, value]) => {
      formData.append("editor_images", value.file);
      formData.append("editor_image_ids", imageId);
    });

    const url = `${API_BASE_URL}/api/lead-excel`;

    const method = "POST";

    try {
      const response = await authFetch(url, {
        method,
        body: formData,
      });

      const result = await response.json();

      if (response.ok) {
        alert("Mail sent successfully");
        formRef.current.reset();
        setSubject("");
        setSubjectOptions([]);
        quill.setText("");
        setAttachments([]);
        setEditorImages({});
        setIsSubmitting(false);
        setLoader(false);
      } else {
        alert(result.error || "Error sending mail");
        setIsSubmitting(false);
        setLoader(false);
      }
    } catch (error) {
      console.error(error);
      alert("Server error");
      setIsSubmitting(false);
      setLoader(false);
    }
  };

  return (
    <div>
      {loader && (
        <div className="loader">
          <div className="loader-spinner"></div>
        </div>
      )}
      <SideBar />
      <div className="main-content">
        <Header />
        <div id="form-content">
          <div id="form-header">
            <p>Lead (Excel Upload)</p>
          </div>
          <hr />
          <form id="form" ref={formRef}>
            <div id="form-input">
              <label htmlFor="excel">
                Upload Excel File<span className="required">*</span>
              </label>
              <input
                type="file"
                name="excel"
                id="excel"
                placeholder="Enter Excel File"
                accept=".xlsx, .xls"
                required
              />
            </div>
            <div id="form-input">
              <label htmlFor="subject">
                Subject<span className="required">*</span>
              </label>
              <input
                type="text"
                name="subject"
                id="subject"
                placeholder="Select Subject"
                list="subject-options"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                autoComplete="off"
                required
              />
              <datalist id="subject-options">
                {subjectOptions.map((option, index) => (
                  <option key={index} value={option} />
                ))}
              </datalist>
            </div>
            <div id="form-input" style={{ width: "100%", flex: "0 0 100%" }}>
              <label htmlFor="content">
                Mail Body<span className="required">*</span>
              </label>

              <div
                ref={quillRef}
                style={{
                  minHeight: "250px",
                  marginBottom: "20px",
                }}
              />
            </div>
          </form>
          <FileUploader
            attachments={attachments}
            setAttachments={setAttachments}
            buttonLabel="Add Attachment"
          />
          <div className="button">
            <button
              type="button"
              className="btn"
              id="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              SEND MAIL
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Lead_Excel;
